import { BigNumber } from "@ethersproject/bignumber"
import { useMemo } from "react"
import { bigNumberToDecimalStringNumber } from "@/common/helpers/bignumber"
import {
  EXCHANGE_RATE_DECIMALS,
  useStakeExchangeRate,
} from "@/stake/hooks/useStakeExchangeRate"

type UseLstGovConversionParams = {
  stakerContractId: string
}

type UseLstGovConversionResult = {
  govTokensToLst: (amount: string) => string
  lstTokensToGov: (amount: string) => string
  isLoading: boolean
  error: Error | null
  exchangeRate: string
}

/**
 * Converts a decimal number to a BigNumber with the specified precision
 * @param decimalStr - Decimal number as string
 * @param decimals - Number of decimal places
 * @returns BigNumber
 */
const decimalToBigNumber = (
  decimalStr: string,
  decimals: number,
): BigNumber => {
  try {
    // If it's an integer, convert it directly
    if (!decimalStr.includes(".")) {
      return BigNumber.from(decimalStr).mul(BigNumber.from(10).pow(decimals))
    }

    // Split integer and decimal parts
    const [integerPart, decimalPart = ""] = decimalStr.split(".")

    // Ensure decimal part has correct length
    const paddedDecimalPart = decimalPart
      .padEnd(decimals, "0")
      .slice(0, decimals)

    // Convert to BigNumber
    const integerValue = BigNumber.from(integerPart || "0").mul(
      BigNumber.from(10).pow(decimals),
    )
    const decimalValue = BigNumber.from(paddedDecimalPart || "0")

    return integerValue.add(decimalValue)
  } catch (error) {
    console.error("Error converting decimal to BigNumber:", error)

    return BigNumber.from(0)
  }
}

/**
 * Pure function to calculate conversion between LST and GOV tokens
 * @param amount - The amount to convert
 * @param exchangeRate - The exchange rate between GOV and LST tokens (1 GOV = exchangeRate LST)
 * @param isLstToGov - Whether converting from LST to GOV (true) or GOV to LST (false)
 * @returns The converted amount as a string
 */
export const calculateLstGovConversion = (
  amount: string,
  exchangeRate: string,
  isLstToGov: boolean,
): string => {
  if (!exchangeRate || !amount) {
    return "0"
  }

  try {
    // Convert amount to BigNumber with 18 decimals (standard token precision)
    const amountBN = decimalToBigNumber(amount, 18)

    // Convert exchangeRate to BigNumber (has EXCHANGE_RATE_DECIMALS precision)
    const exchangeRateBN = BigNumber.from(exchangeRate)

    if (isLstToGov) {
      // Converting from LST to GOV: amount * 10^EXCHANGE_RATE_DECIMALS / exchangeRate
      // To maintain precision, first multiply by 10^EXCHANGE_RATE_DECIMALS
      const scaledAmount = amountBN.mul(
        BigNumber.from(10).pow(EXCHANGE_RATE_DECIMALS),
      )
      const result = scaledAmount.div(exchangeRateBN)

      return bigNumberToDecimalStringNumber(result, 18)
    } else {
      // Converting from GOV to LST: amount * exchangeRate / 10^EXCHANGE_RATE_DECIMALS
      const result = amountBN
        .mul(exchangeRateBN)
        .div(BigNumber.from(10).pow(EXCHANGE_RATE_DECIMALS))

      return bigNumberToDecimalStringNumber(result, 18)
    }
  } catch (err) {
    console.error("Error calculating conversion:", err)

    return "0"
  }
}

/**
 * Hook to calculate conversion between LST and GOV tokens
 * @param stakerContractId - The staker contract ID
 * @returns Methods to convert between GOV and LST tokens, loading state, and error
 */
export const useLstGovConversion = ({
  stakerContractId,
}: UseLstGovConversionParams): UseLstGovConversionResult => {
  // Get the exchange rate
  const { data, isLoading, error } = useStakeExchangeRate(stakerContractId)

  const exchangeRate = data?.stakeExchangeRate

  // Create memoized conversion functions
  const govTokensToLst = useMemo(() => {
    return (amount: string): string => {
      if (!exchangeRate || !amount || !stakerContractId) {
        return "0"
      }

      // Convert from GOV to LST (isLstToGov = false)
      return calculateLstGovConversion(amount, exchangeRate, false)
    }
  }, [exchangeRate, stakerContractId])

  const lstTokensToGov = useMemo(() => {
    return (amount: string): string => {
      if (!exchangeRate || !amount || !stakerContractId) {
        return "0"
      }

      // Convert from LST to GOV (isLstToGov = true)
      return calculateLstGovConversion(amount, exchangeRate, true)
    }
  }, [exchangeRate, stakerContractId])

  return {
    govTokensToLst,
    lstTokensToGov,
    isLoading,
    error,
    exchangeRate:
      !isLoading && exchangeRate
        ? bigNumberToDecimalStringNumber(
            BigNumber.from(exchangeRate),
            EXCHANGE_RATE_DECIMALS,
          )
        : "",
  }
}
